import { useId } from "react";
import styles from "./Fieldset.module.scss";
import { useFormState } from "react-hook-form";
import clsx from "clsx";

interface FieldsetProps {
	legend: string;
	className?: string;
	innerClassName?: string;
	children?: React.ReactNode;
	asterisk?: boolean;
	errorDeps?: string[];
	/**
	 * Marks this fieldset as an analytics funnel stage. Read by a delegated
	 * focus handler on the form, so nested fieldsets left unset are attributed
	 * to their enclosing section rather than counting as a stage of their own.
	 */
	sectionId?: string;
}

export const Fieldset = ({
	legend,
	className,
	innerClassName,
	children,
	asterisk,
	errorDeps,
	sectionId,
}: FieldsetProps) => {
	const id = useId();

	const { errors } = useFormState();

	const hasError = errorDeps?.some(dep => errors[dep]);

	return (
		<div
			className={clsx(styles["fieldset"], hasError && styles["fieldset--error"], className)}
			role="group"
			aria-labelledby={id}
			data-section={sectionId}
		>
			<div className={styles["fieldset__header"]}>
				<span
					className={styles["fieldset__legend"]}
					id={id}
				>
					{legend}
					{asterisk && <span className={styles["fieldset__asterisk"]}>*</span>}
				</span>
			</div>

			<div className={clsx(styles["fieldset__inner"], innerClassName)}>{children}</div>
		</div>
	);
};
